import { t } from "@/languages/i18n";
import { Divider, Form, FormInstance, Input, Select } from "antd";
import { ImageUploadField } from "../ui-builder/config/lib/ImageUploadField";
const { TextArea } = Input;

interface LogoState {
  headerLogoUrl: string;
  headerLogoId: number | null;
  footerLogoUrl: string;
  footerLogoId: number | null;
}

interface SettingsFormFileds {
  form: FormInstance;
  isMedia: boolean;
  categoryOptions?: any[];
  loadingCategories?: boolean;
  hasMoreCategories?: boolean;
  categoriesLength?: number;
  totalCategories?: number;
  loadMoreCategories?: () => void;
  logoState?: LogoState;
  onLogoChange?: (field: "headerLogoUrl" | "footerLogoUrl", value: string, id: number | null) => void;
  faviconUrl?: string | null;
  faviconId?: number | null;
  onFaviconChange?: (url: string, id: number | null) => void;
}

interface LogoPickerProps {
  label: string;
  value: string;
  onChange: (value: string, id: number | null) => void;
}

function LogoPicker({ label, value, onChange }: LogoPickerProps) {
  return (
    <Form.Item label={label}>
      <ImageUploadField
        value={value}
        hideUrl={true}
        onMediaChange={(url, id) => onChange(url, id)}
      />
    </Form.Item>
  );
}

const SettingsFormFileds = ({
  form,
  isMedia,
  categoryOptions,
  loadingCategories,
  hasMoreCategories,
  categoriesLength,
  totalCategories,
  loadMoreCategories,
  logoState,
  onLogoChange,
  faviconUrl,
  faviconId,
  onFaviconChange,
}: SettingsFormFileds) => {
  if (isMedia) {
    return (
      <>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label={t("Phone number")}
            name="phoneNumber"
            rules={[{ required: true, message: t("Please input phone number!") }]}
          >
            <Input placeholder={t("Please enter phone number")} />
          </Form.Item>

          <Form.Item
            label={t("Tagline")}
            name="tagline"
            rules={[{ required: true, message: t("Please input tagline!") }]}
          >
            <Input placeholder={t("Please enter tagline")} />
          </Form.Item>

          <Form.Item label={t("Facebook")} name="facebook">
            <Input placeholder={t("Please enter Facebook")} />
          </Form.Item>

          <Form.Item label={t("Twitter")} name="twitter">
            <Input placeholder={t("Please enter Twitter")} />
          </Form.Item>

          <Form.Item label={t("LinkedIn")} name="linkedIn">
            <Input placeholder={t("Please enter LinkedIn")} />
          </Form.Item>

          <Form.Item label={t("Hotline")} name="hotline">
            <Input placeholder={t("Please enter Hotline")} />
          </Form.Item>

          <Form.Item label={t("Email")} name="email">
            <Input type="email" placeholder={t("Please enter Email")} />
          </Form.Item>

          <Form.Item label={t("Advertising email")} name="advertisingEmail">
            <Input type="email" placeholder={t("Please enter Advertising email")} />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label={t("Featured news category")} name="featuredNewsId">
            <Select
              showSearch
              placeholder={t("Please select featured news category")}
              options={categoryOptions}
              loading={loadingCategories}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              popupRender={(menu) => (
                <>
                  {menu}
                  {hasMoreCategories && (
                    <>
                      <Divider style={{ margin: "4px 0" }} />
                      <div
                        style={{ padding: "4px 8px", textAlign: "center", cursor: "pointer" }}
                        onClick={loadMoreCategories}
                      >
                        <span style={{ color: "#1890ff", fontSize: "14px" }}>
                          {loadingCategories
                            ? t("Loading...")
                            : `${t("View more")} (${categoriesLength}/${totalCategories})`}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}
            />
          </Form.Item>

          <Form.Item label={t("Hot news category")} name="hotNewsId">
            <Select
              showSearch
              placeholder={t("Please select hot news category")}
              options={categoryOptions}
              loading={loadingCategories}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              allowClear
              popupRender={(menu) => (
                <>
                  {menu}
                  {hasMoreCategories && (
                    <>
                      <Divider style={{ margin: "4px 0" }} />
                      <div
                        style={{ padding: "4px 8px", textAlign: "center", cursor: "pointer" }}
                        onClick={loadMoreCategories}
                      >
                        <span style={{ color: "#1890ff", fontSize: "14px" }}>
                          {loadingCategories
                            ? t("Loading...")
                            : `${t("View more")} (${categoriesLength}/${totalCategories})`}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}
            />
          </Form.Item>
        </div>

        <Form.Item label={t("Header Scripts")} name="headerScripts">
          <TextArea
            placeholder={t("Enter header scripts")}
            rows={5}
            style={{ fontFamily: "monospace" }}
          />
        </Form.Item>

        <Form.Item label={t("GoogleAnalyticsTrackingID")} name="GoogleAnalyticsTrackingID">
          <Input placeholder={t("Please enter GoogleAnalyticsTrackingID")} />
        </Form.Item>

        <Form.Item label={t("Youtube")} name="youtube">
          <Input placeholder={t("Please enter Youtube")} />
        </Form.Item>

        <Form.Item label={t("Body Scripts")} name="bodyScript">
          <TextArea
            placeholder={t("Enter body scripts")}
            rows={5}
            style={{ fontFamily: "monospace" }}
          />
        </Form.Item>

        <div className="grid grid-cols-3 gap-4">
          <LogoPicker
            label={t("Header Logo")}
            value={logoState?.headerLogoUrl ?? ""}
            onChange={(value, id) => onLogoChange?.("headerLogoUrl", value, id)}
          />
          <LogoPicker
            label={t("Footer Logo")}
            value={logoState?.footerLogoUrl ?? ""}
            onChange={(value, id) => onLogoChange?.("footerLogoUrl", value, id)}
          />
          <Form.Item label={t("Favicon")}>
            <ImageUploadField
              value={faviconUrl ?? ""}
              hideUrl={true}
              onMediaChange={(url, id) => onFaviconChange?.(url, id ?? null)}
            />
          </Form.Item>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Form.Item label={t("Hotline")} name="hotline">
          <Input placeholder={t("Please enter Hotline")} />
        </Form.Item>

        <Form.Item
          label={t("Address")}
          name="address"
          rules={[{ required: true, message: t("Please input address!") }]}
        >
          <Input placeholder={t("Enter address")} />
        </Form.Item>

        <Form.Item label={t("Email")} name="email">
          <Input type="email" placeholder={t("Please enter Email")} />
        </Form.Item>

        <Form.Item label={t("Facebook URL")} name="facebookUrl">
          <Input placeholder={t("Enter Facebook URL")} />
        </Form.Item>

        <Form.Item label={t("Twitter URL")} name="twitterUrl">
          <Input placeholder={t("Enter Twitter URL")} />
        </Form.Item>

        <Form.Item label={t("Youtube URL")} name="youtubeUrl">
          <Input placeholder={t("Enter Youtube URL")} />
        </Form.Item>

        <Form.Item label={t("GoogleAnalyticsTrackingID")} name="GoogleAnalyticsTrackingID">
          <Input placeholder={t("Please enter GoogleAnalyticsTrackingID")} />
        </Form.Item>

        <Form.Item label={t("bccToEmail")} name="bccToEmail">
          <Input type="email" placeholder={t("Enter bccToEmail")} />
        </Form.Item>

        <Form.Item label={t("ccToEmail")} name="ccToEmail">
          <Input type="email" placeholder={t("Enter ccToEmail")} />
        </Form.Item>

        <Form.Item label={t("googleMapUrl")} name="googleMapUrl">
          <Input placeholder={t("Enter Google Map Url")} />
        </Form.Item>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <LogoPicker
          label={t("Header Logo")}
          value={logoState?.headerLogoUrl ?? ""}
          onChange={(value, id) => onLogoChange?.("headerLogoUrl", value, id)}
        />
        <LogoPicker
          label={t("Footer Logo")}
          value={logoState?.footerLogoUrl ?? ""}
          onChange={(value, id) => onLogoChange?.("footerLogoUrl", value, id)}
        />
        <Form.Item label={t("Favicon")}>
          <ImageUploadField
            value={faviconUrl ?? ""}
            hideUrl={true}
            onMediaChange={(url, id) => onFaviconChange?.(url, id ?? null)}
          />
        </Form.Item>
      </div>

      <Form.Item label={t("Header Script")} name="headerScript">
        <TextArea
          placeholder={t("Enter header script")}
          rows={5}
          style={{ fontFamily: "monospace" }}
        />
      </Form.Item>

      <Form.Item label={t("Body Script")} name="bodyScript">
        <TextArea
          placeholder={t("Enter body script")}
          rows={5}
          style={{ fontFamily: "monospace" }}
        />
      </Form.Item>
    </>
  );
};

export default SettingsFormFileds;
